import React, { useState, useEffect, useMemo } from 'react';

const ResetOverlay = ({ isOpen, onComplete, onClose, totalResets }) => {
  const [stage, setStage] = useState('intro');
  const [phase, setPhase] = useState('inhale');
  const [cycleCount, setCycleCount] = useState(1);
  const [progress, setProgress] = useState(0);
  const [countdown, setCountdown] = useState(5);

  const TOTAL_CYCLES = 10;
  const INHALE_TIME = 3000;
  const HOLD_TIME = 5000;
  const EXHALE_TIME = 5000;
  const COMPLETE_DISPLAY_TIME = 3000;

  const phaseData = useMemo(() => ({
    inhale: {
      label: 'ВДОХ',
      hint: 'Стопы в пол. Воздух в живот.',
      duration: INHALE_TIME,
    },
    hold: {
      label: 'ПАУЗА',
      hint: 'Ось. Позвоночник. Вертикаль.',
      duration: HOLD_TIME,
    },
    exhale: {
      label: 'ВЫДОХ',
      hint: 'Отпусти. Напряжение в землю.',
      duration: EXHALE_TIME,
    },
  }), []);

  useEffect(() => {
    if (!isOpen) {
      setStage('intro');
      setPhase('inhale');
      setCycleCount(1);
      setProgress(0);
      setCountdown(5);
    }
  }, [isOpen]);

  useEffect(() => {
    if (stage !== 'countdown') return;

    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setStage('breathing');
      setPhase('inhale');
      setCycleCount(1);
      setProgress(0);
    }
  }, [stage, countdown]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (stage !== 'breathing') return;
    
    const duration = phaseData[phase].duration;
    const interval = 50;
    const step = (interval / duration) * 100;

    setProgress(0);

    const progressTimer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressTimer);
          return 100;
        }
        return prev + step;
      });
    }, interval);

    const phaseTimer = setTimeout(() => {
      if (phase === 'inhale') {
        setPhase('hold');
      } else if (phase === 'hold') {
        setPhase('exhale');
      } else if (phase === 'exhale') {
        if (cycleCount < TOTAL_CYCLES) {
          setCycleCount(prev => prev + 1);
          setPhase('inhale');
        } else {
          setStage('complete');
        }
      }
    }, duration);

    return () => {
      clearInterval(progressTimer);
      clearTimeout(phaseTimer);
    };
  }, [stage, phase, cycleCount, phaseData]);

  useEffect(() => {
    if (stage === 'complete') {
      const timer = setTimeout(() => {
        onComplete();
      }, COMPLETE_DISPLAY_TIME);
      return () => clearTimeout(timer);
    }
  }, [stage, onComplete]);

  const handleStart = () => {
    setStage('countdown');
    setCountdown(5);
  };

  if (!isOpen) return null;

  const circumference = 2 * Math.PI * 54;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <button style={styles.closeButton} onClick={onClose}>
          ✕
        </button>

        <div style={styles.iconContainer}>
          <span style={styles.icon}>🔄</span>
        </div>

        <h2 style={styles.title}>ЗАЗЕМЛЕНИЕ</h2>
        <p style={styles.subtitle}>Микро-практика для сброса состояния</p>

        {stage === 'intro' && (
          <>
            <p style={styles.introText}>
              {TOTAL_CYCLES} циклов дыхания с фокусом на ось тела.
              <br />
              Отпусти напряжение в землю.
            </p>

            <button style={styles.startButton} onClick={handleStart}>
              НАЧАТЬ ПРАКТИКУ
            </button>

            <p style={styles.resetCount}>
              Выполнено ресетов: <span style={styles.resetNumber}>{totalResets}</span>
            </p>
          </>
        )}

        {stage === 'countdown' && (
          <div style={styles.countdownContainer}>
            <div style={styles.countdownCircle}>
              <span style={styles.countdownNumber}>{countdown}</span>
            </div>
            <p style={styles.countdownHint}>Приготовься...</p>
          </div>
        )}

        {stage === 'breathing' && (
          <>
            <div style={styles.circleContainer}>
              <svg width="120" height="120" style={styles.progressSvg}>
                <circle
                  cx="60"
                  cy="60"
                  r="54"
                  stroke="rgba(255, 255, 255, 0.1)"
                  strokeWidth="4"
                  fill="none"
                />
                <circle
                  cx="60"
                  cy="60"
                  r="54"
                  stroke="#E5D2A0"
                  strokeWidth="4"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  style={{
                    transform: 'rotate(-90deg)',
                    transformOrigin: '50% 50%',
                    transition: 'stroke-dashoffset 0.05s linear',
                  }}
                />
              </svg>
              <div style={styles.circleContent}>
                <span style={styles.phaseLabel}>{phaseData[phase].label}</span>
                <span style={styles.cycleLabel}>{cycleCount}/{TOTAL_CYCLES}</span>
              </div>
            </div>

            <p style={styles.hint}>{phaseData[phase].hint}</p>
          </>
        )}

        {stage === 'complete' && (
          <div style={styles.completeContainer}>
            <div style={styles.completeIcon}>✧</div>
            <p style={styles.completeText}>
              Заземление совершено.
              <br />
              Следуй далее по своим делам.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modal: {
    background: '#1a1a1a',
    borderRadius: '20px',
    padding: '32px 24px',
    maxWidth: '340px',
    width: '100%',
    textAlign: 'center',
    position: 'relative',
  },
  closeButton: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    width: '32px',
    height: '32px',
    border: 'none',
    background: 'transparent',
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: '18px',
    cursor: 'pointer',
  },
  iconContainer: {
    marginBottom: '16px',
  },
  icon: {
    fontSize: '32px',
  },
  title: {
    color: '#E5D2A0',
    fontSize: '18px',
    fontWeight: '600',
    letterSpacing: '4px',
    margin: '0 0 8px 0',
  },
  subtitle: {
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: '13px',
    margin: '0 0 24px 0',
  },
  introText: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: '15px',
    lineHeight: '1.6',
    margin: '0 0 24px 0',
  },
  startButton: {
    width: '100%',
    padding: '16px',
    background: '#E5D2A0',
    border: 'none',
    borderRadius: '12px',
    color: '#000',
    fontSize: '14px',
    fontWeight: '600',
    letterSpacing: '2px',
    cursor: 'pointer',
    marginBottom: '20px',
  },
  resetCount: {
    color: 'rgba(255, 255, 255, 0.4)',
    fontSize: '13px',
    margin: 0,
  },
  resetNumber: {
    color: '#E5D2A0',
  },
  countdownContainer: {
    padding: '20px 0',
  },
  countdownCircle: {
    width: '100px',
    height: '100px',
    borderRadius: '50%',
    borderWidth: '3px',
    borderStyle: 'solid',
    borderColor: '#E5D2A0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
  },
  countdownNumber: {
    color: '#E5D2A0',
    fontSize: '48px',
    fontWeight: '300',
  },
  countdownHint: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: '16px',
    margin: 0,
  },
  circleContainer: {
    position: 'relative',
    width: '120px',
    height: '120px',
    margin: '0 auto 24px',
  },
  progressSvg: {
    position: 'absolute',
    top: 0,
    left: 0,
  },
  circleContent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  phaseLabel: {
    color: '#FFFFFF',
    fontSize: '20px',
    fontWeight: '600',
    letterSpacing: '2px',
  },
  cycleLabel: {
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: '13px',
    marginTop: '4px',
  },
  hint: {
    color: '#E5D2A0',
    fontSize: '15px',
    fontWeight: '500',
    margin: 0,
  },
  completeContainer: {
    padding: '20px 0',
  },
  completeIcon: {
    fontSize: '40px',
    color: '#E5D2A0',
    marginBottom: '16px',
  },
  completeText: {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: '16px',
    lineHeight: '1.7',
    margin: 0,
  },
};

export default ResetOverlay;